import { ShiftDetail } from 'api/services/shift/schema/shift-detail.schema';
import { ApplicationDetails, ClaimDetails, SkillType, VAT } from '@types';
import { HistoryEvent } from '../components/shift-detail-modal/components/shift-detail-head/shift-info-button/types';

export type ShiftDetailResponse = {
  id: string; // Shift UUID
  name: string;

  companyId: string;
  companyName: string;
  branchOfficeName: string;
  departmentName: string;
  departmentId: string;
  jobTypeId: string | null;

  isoEndDateTime: string;
  isoStartDateTime: string;
  startTime: string; // e.g. "16:00"
  endTime: string; // e.g. "18:00"
  startDate: string; // e.g. "2021-03-04"
  endDate: string; // e.g. "2021-03-04"
  mutableUntilDate: string; // e.g. "2021-03-04T14:00:00.000Z"

  workCooldownMinutes: number; // filled with workCooldownMinutes from company
  breakMinutes: number; // filled with breakMinutes from <Job>; can be edited in <Shift>

  maxClaimants: number; // e.g. "5"
  flexPools: { id: string; name: string; directClaimAllowed: boolean }[];
  flexPoolOnly: boolean;
  vat: VAT;
  location: {
    id: string;
    address: string;
    city: string | null;
    country: string | null;
    houseNumber: string | null;
    postalCode: string | null;
    street: string | null;
    latitude: number | null;
    longitude: number | null;
  };
  contactName: string;
  contactPhoneNumber: string;
  history: HistoryEvent[];

  claimants: ClaimDetails[]; // Empty array when no claimants
  applicants: ApplicationDetails[]; // Empty array when no applicants
  isFreelance: boolean;
  isTempwork: boolean;
  isEmployee: boolean;
  freelanceProperties?: FreelanceShiftProperties;
  tempWorkProperties?: TempWorkShiftProperties;

  imageUrl: string | null;
  descriptionShort: string;
  descriptionLong: string;
  descriptionLawRequirements: string;
  shiftCertificates: { name: string; id: string }[];
  jobTypeCertificates: { name: string; id: string }[];
  requirements: { id: string; name: string; type: SkillType }[];
  companyGrossHourlyRate?: number;
  isOrtApplicable: boolean;
  costCenter: string | null;
};

export type FreelanceShiftProperties = {
  hourlyRateCents: number;
  mutableUntilMinutes: number;
  ortApplicable: boolean;
};

export type TempWorkShiftProperties = {
  grossHourlyRateCents: number;
  cla: string;
  ortApplicable: boolean;
};

export type ShiftDetailJob = {
  id: string; // Job UUID
  flexPoolOnly: boolean;
  name: string; // e.g. "Beveiliger",
  companyId: string;
  companyName: string; // e.g. "Felyx"
  location: string; // e.g. "Rotterdam"

  department: {
    id: string; // BranchOffice UUID
    departmentName: string; // e.g. "Felyx Vlaardingen"
  };
};

export const isFreelanceShift = (
  shift: ShiftDetail
): shift is ShiftDetail & { freelanceProperties: FreelanceShiftProperties } =>
  shift.freelanceProperties !== undefined;

export const isTempworkShift = (
  shift: ShiftDetail
): shift is ShiftDetail & { tempWorkProperties: TempWorkShiftProperties } =>
  shift.tempWorkProperties !== undefined;

export const isEmployeeShift = (
  shift: ShiftDetail
): shift is ShiftDetail & { isEmployee: boolean } => !!shift.isEmployee;
